export type LiveUpdateStatus = "connected" | "reconnecting" | "stopped";

export class LiveEventTerminalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LiveEventTerminalError";
  }
}

export class LiveEventRevokedError extends LiveEventTerminalError {
  constructor() {
    super("VibeNotes access was revoked from your node.");
    this.name = "LiveEventRevokedError";
  }
}

export interface LiveEventEnvironment {
  onlineTarget?: EventTarget;
  visibilityTarget?: EventTarget;
  isVisible?: () => boolean;
  retryBaseDelayMs?: number;
  retryMaxDelayMs?: number;
  random?: () => number;
}

interface LiveEventLoopOptions {
  open(signal: AbortSignal): Promise<ReadableStream<Uint8Array>>;
  acceptsChange(change: Record<string, unknown>): boolean;
  refresh(): Promise<void>;
  signal: AbortSignal;
  onStatus?: (status: LiveUpdateStatus) => void;
  environment?: LiveEventEnvironment;
}

const DEFAULT_RETRY_BASE_DELAY_MS = 500;
const DEFAULT_RETRY_MAX_DELAY_MS = 15_000;
const STABLE_CONNECTION_MS = 30_000;

export async function runLiveEventLoop(options: LiveEventLoopOptions): Promise<void> {
  const environment = options.environment ?? {};
  const onlineTarget = environment.onlineTarget ?? (typeof window === "undefined" ? undefined : window);
  const visibilityTarget = environment.visibilityTarget ?? (typeof document === "undefined" ? undefined : document);
  const isVisible = environment.isVisible ?? (() => typeof document !== "undefined" && document.visibilityState === "visible");
  const retryBaseDelayMs = environment.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;
  const retryMaxDelayMs = environment.retryMaxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS;
  const random = environment.random ?? Math.random;
  let currentAttempt: AbortController | null = null;
  let wakeRetry: () => void = () => undefined;
  let wakeGeneration = 0;
  let handledWakeGeneration = 0;
  let retryAttempt = 0;
  let needsRefresh = false;
  let lastStatus: LiveUpdateStatus | null = null;
  let connectedAt: number | null = null;

  const setStatus = (status: LiveUpdateStatus) => {
    if (lastStatus === status || options.signal.aborted) return;
    lastStatus = status;
    options.onStatus?.(status);
  };
  const requestImmediateRetry = () => {
    if (options.signal.aborted) return;
    wakeGeneration += 1;
    currentAttempt?.abort();
    wakeRetry();
  };
  const onVisible = () => {
    if (isVisible()) requestImmediateRetry();
  };

  onlineTarget?.addEventListener("online", requestImmediateRetry);
  visibilityTarget?.addEventListener("visibilitychange", onVisible);
  try {
    while (!options.signal.aborted) {
      const attempt = new AbortController();
      const abortAttempt = () => attempt.abort();
      options.signal.addEventListener("abort", abortAttempt, { once: true });
      currentAttempt = attempt;
      try {
        const body = await options.open(attempt.signal);
        if (needsRefresh) await options.refresh();
        if (attempt.signal.aborted) throw abortError();
        needsRefresh = false;
        connectedAt = Date.now();
        setStatus("connected");
        await consumeEventStream(body, options.acceptsChange, options.refresh, attempt.signal);
        throw new Error("Live update stream ended.");
      } catch (error) {
        if (options.signal.aborted) return;
        if (error instanceof LiveEventTerminalError) {
          setStatus("stopped");
          throw error;
        }
        if (connectedAt !== null && Date.now() - connectedAt >= STABLE_CONNECTION_MS) retryAttempt = 0;
        connectedAt = null;
        needsRefresh = true;
        setStatus("reconnecting");
      } finally {
        currentAttempt = null;
        options.signal.removeEventListener("abort", abortAttempt);
      }

      const retryImmediately = wakeGeneration > handledWakeGeneration;
      handledWakeGeneration = wakeGeneration;
      if (!retryImmediately) {
        const exponentialDelay = Math.min(retryMaxDelayMs, retryBaseDelayMs * (2 ** Math.min(retryAttempt, 10)));
        const jitteredDelay = Math.min(
          retryMaxDelayMs,
          Math.max(0, Math.round(exponentialDelay * (0.75 + random() * 0.5))),
        );
        await waitForRetry(jitteredDelay, options.signal, (wake) => { wakeRetry = wake; });
        wakeRetry = () => undefined;
        handledWakeGeneration = wakeGeneration;
        retryAttempt += 1;
      }
    }
  } finally {
    currentAttempt?.abort();
    wakeRetry();
    onlineTarget?.removeEventListener("online", requestImmediateRetry);
    visibilityTarget?.removeEventListener("visibilitychange", onVisible);
  }
}

async function consumeEventStream(
  body: ReadableStream<Uint8Array>,
  acceptsChange: (change: Record<string, unknown>) => boolean,
  refresh: () => Promise<void>,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await readWithSignal(reader, signal);
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const event = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const type = event.match(/^event:\s*(.+)$/mu)?.[1];
        const data = event.match(/^data:\s*(.+)$/mu)?.[1];
        if (type === "revoked") throw new LiveEventRevokedError();
        if (type === "change" && data) {
          const change = JSON.parse(data) as Record<string, unknown>;
          if (acceptsChange(change)) await refresh();
        }
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    if (signal.aborted) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

async function readWithSignal<T>(
  reader: ReadableStreamDefaultReader<T>,
  signal: AbortSignal,
): Promise<ReadableStreamReadResult<T>> {
  if (signal.aborted) throw abortError();
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener("abort", onAbort, { once: true });
    reader.read().then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

async function waitForRetry(
  delayMs: number,
  signal: AbortSignal,
  registerWake: (wake: () => void) => void,
): Promise<void> {
  if (signal.aborted) return;
  await new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = () => {
      if (timer !== null) clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    };
    timer = setTimeout(finish, delayMs);
    signal.addEventListener("abort", finish, { once: true });
    registerWake(finish);
  });
}

function abortError(): Error {
  if (typeof DOMException !== "undefined") return new DOMException("The operation was aborted.", "AbortError");
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  return error;
}
